import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Modal, Row, Col, Select, DatePicker, InputNumber, message } from 'antd'
import moment from 'moment'
import { constants } from '../../constants'
import serviceInterface from '../../services/service.interface'
import ImageSwapFigure from '../exercise/ImageSwapFigure'
import {
  cardStyle,
  sectionHeaderRow,
  sectionTitle,
  fieldLabelStyle,
  overriddenDot,
} from './tracpatch.config.styles'
import { currentWeekFromSurgery } from './tracpatch.week.defaults'
import {
  PROGRESS_GOAL_WEEKS,
  PROGRESS_GOAL_MAX_WEEK,
  resolveGoalCell,
  isGoalCellOverridden,
  setGoalCell,
} from './progress.goals.defaults'

const tableHeaderStyle = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  color: '#64748b',
  whiteSpace: 'nowrap',
}

// Frame conversion, mirroring physio_app_web ProgressTestPage.js so the preview
// dial matches the patient view exactly. Goal values are DISPLAYED/clinical
// degrees (0° = full extension, 90° = seated rest). The figure + dial run in an
// internal frame = measured + PROGRESS_DEGREE_OFFSET (rest lands on internal 45).
const PROGRESS_DEGREE_OFFSET = 45
const PROGRESS_REST_DISPLAY_DEG = 90
// displayed clinical angle → raw measured angle (inverse of toDisplay)
const toMeasured = (d) =>
  d >= PROGRESS_REST_DISPLAY_DEG
    ? d - PROGRESS_REST_DISPLAY_DEG
    : (d - PROGRESS_REST_DISPLAY_DEG) / 2
// displayed clinical angle → internal figure/dial angle
const toInternal = (d) => toMeasured(d) + PROGRESS_DEGREE_OFFSET

/**
 * Modal opened from the TracPatch row on the admin Device Schedule page.
 *
 * Left pane: the device's schedule (start / end / interval) + a per-week
 * editor for the patient's Progress-Test ROM goals (extension + flexion).
 * Right pane: the patient avatar (HEEL_SLIDE_ROM image-swap figure) with a
 * preview-angle slider and the selected week's goal window drawn as a band —
 * mirrors the layout of tracpatch.config.modal.js.
 *
 * Props:
 *   visible          — bool
 *   patient          — { surgery: { date } } for date math + current-week seed
 *   editpage         — true on /patient/deviceschedule (dates are real dates);
 *                      false in the add-patient flow (dates are day offsets)
 *   initialCriteria  — device.scheduled_criteria (start_date/end_date are day
 *                      offsets relative to surgery; interval/time/skip flags)
 *   initialOverrides — the patient's progressTestGoalOverrides array
 *   onCancel()       — close without saving
 *   onSave(criteria, overridesArray) — criteria.start_date/end_date are day
 *                      offsets (same shape as scheduled_criteria)
 */
const ProgressGoalsModal = (props) => {
  const editpage = !!props.editpage
  const surgeryDate = props.patient && props.patient.surgery && props.patient.surgery.date

  // Day-offset → moment date for the DatePicker initial values.
  const offsetToDate = (offset) => moment(surgeryDate).add(offset || 0, 'd')
  // moment date → day offset relative to surgery (mirror of getDays in
  // device.edit.card.js).
  const dateToOffset = (date) =>
    moment(date).startOf('day').diff(moment(surgeryDate).startOf('day'), 'days')

  const init = props.initialCriteria || {}
  const [startDate, setStartDate] = useState(
    editpage ? offsetToDate(init.start_date) : init.start_date
  )
  const [endDate, setEndDate] = useState(
    editpage ? offsetToDate(init.end_date) : init.end_date
  )
  const [interval, setIntervalVal] = useState(
    init.interval >= 0 ? init.interval : 1
  )

  const [overrides, setOverrides] = useState(
    Array.isArray(props.initialOverrides) ? props.initialOverrides : []
  )

  // Clinic-level Progress-Test goals (set on the clinic settings page). These
  // are the BASE this patient inherits where they have no per-patient override,
  // so the table shows the clinic's goals — not the hardcoded MoveAI defaults —
  // exactly as the patient app resolves them (patient → clinic → hardcoded).
  const [clinicOverrides, setClinicOverrides] = useState([])
  // Resolve the clinic id to read its Progress-Test goals. The device-schedule
  // nav-state patient does NOT carry `customer`, so prefer the logged-in clinic
  // context from Redux (the SAME source the Clinic Settings page uses, so the
  // modal reads exactly the clinic whose goals were edited); fall back to the
  // patient's own customer if it happens to be populated.
  const patientCustomer = props.patient && props.patient.customer
  const customerid =
    props.reduxCustomerId ||
    (patientCustomer && (patientCustomer._id || patientCustomer)) ||
    null

  useEffect(() => {
    let cancelled = false
    if (!props.visible || !customerid) return undefined
    serviceInterface
      .SettingsService()
      .getClinicSetting({ customerid, key: 'progress-test-goals' })
      .then((res) => {
        if (cancelled) return
        const value = res && res.data && res.data.value
        const list = value && Array.isArray(value.overrides) ? value.overrides : []
        setClinicOverrides(list)
      })
      .catch(() => {
        if (!cancelled) setClinicOverrides([])
      })
    return () => {
      cancelled = true
    }
  }, [props.visible, customerid])

  const initialWeek = currentWeekFromSurgery(surgeryDate, PROGRESS_GOAL_MAX_WEEK)
  const [selectedWeek, setSelectedWeek] = useState(initialWeek)
  // Preview angle is a DISPLAYED clinical angle (what the patient reads). Starts
  // at seated rest (90°) so the figure opens on the seated ROM-test pose.
  const [previewAngle, setPreviewAngle] = useState(PROGRESS_REST_DISPLAY_DEG)

  const cellValue = (week, field) =>
    resolveGoalCell(week, field, overrides, clinicOverrides)
  const cellOverridden = (week, field) =>
    isGoalCellOverridden(week, field, overrides, clinicOverrides)

  const setCell = (week, field, value) => {
    setOverrides((prev) => setGoalCell(prev, week, field, value, clinicOverrides))
  }

  // The selected week's resolved goals (DISPLAYED degrees) become dotted-arc
  // weekly-goal markers on the dial — same UI as the patient Progress Test:
  // a full yellow dotted ROM arc plus a thin green marker at each goal.
  const goalExt = cellValue(selectedWeek, 'extension')
  const goalFlex = cellValue(selectedWeek, 'flexion')
  const goalAngles = []
  if (goalExt != null) goalAngles.push(toInternal(goalExt))
  if (goalFlex != null) goalAngles.push(toInternal(goalFlex))

  // Internal figure/dial angle for the preview slider's displayed value.
  const figureAngle = toInternal(previewAngle)

  // Slider reads in DISPLAYED degrees (0° extension … 160° deep flexion).
  const sliderMin = 0
  const sliderMax = 160

  const handleOk = () => {
    let startOffset = startDate
    let endOffset = endDate
    if (editpage) {
      if (startDate == null || endDate == null) {
        message.error('start date or end date is missing')
        return
      }
      startOffset = dateToOffset(startDate)
      endOffset = dateToOffset(endDate)
    }
    if (startOffset == null || endOffset == null) {
      message.error('start date or end date is missing')
      return
    }
    if (startOffset > endOffset) {
      message.error('start date can be same or before end date')
      return
    }
    const criteria = {
      ...init,
      start_date: startOffset,
      end_date: endOffset,
      interval,
      time: init.time || 1,
    }
    props.onSave(criteria, overrides)
  }

  return (
    <Modal
      visible={props.visible}
      title='TracPatch — Schedule & Progress-Test Goals'
      onCancel={props.onCancel}
      onOk={handleOk}
      okText='Save'
      cancelText='Cancel'
      width='75%'
      destroyOnClose
      bodyStyle={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
    >
      <Row gutter={16} style={{ flexWrap: 'nowrap' }}>
        <Col flex='1 1 0' style={{ minWidth: 0 }}>
          {/* SCHEDULE */}
          <div style={cardStyle}>
            <div style={sectionHeaderRow}>
              <span style={sectionTitle}>SCHEDULE</span>
            </div>
            <Row gutter={16}>
              <Col span={8}>
                <div style={fieldLabelStyle}>Start</div>
                {editpage ? (
                  <DatePicker
                    showToday={false}
                    value={startDate}
                    onChange={setStartDate}
                    style={{ width: '100%' }}
                  />
                ) : (
                  <InputNumber
                    type='number'
                    value={startDate}
                    onChange={setStartDate}
                    style={{ width: '100%' }}
                  />
                )}
              </Col>
              <Col span={8}>
                <div style={fieldLabelStyle}>End</div>
                {editpage ? (
                  <DatePicker
                    showToday={false}
                    value={endDate}
                    onChange={setEndDate}
                    style={{ width: '100%' }}
                  />
                ) : (
                  <InputNumber
                    type='number'
                    value={endDate}
                    onChange={setEndDate}
                    style={{ width: '100%' }}
                  />
                )}
              </Col>
              <Col span={8}>
                <div style={fieldLabelStyle}>Interval</div>
                <Select
                  value={interval}
                  onChange={setIntervalVal}
                  style={{ width: '100%' }}
                >
                  {constants.INTERVAL_OPTIONS.map((o) => (
                    <Select.Option key={o.index} value={o.index}>
                      {o.name}
                    </Select.Option>
                  ))}
                </Select>
              </Col>
            </Row>
          </div>

          {/* WEEKLY GOALS */}
          <div style={cardStyle}>
            <div style={sectionHeaderRow}>
              <span style={sectionTitle}>PROGRESS-TEST WEEKLY GOALS</span>
            </div>
            <div
              style={{
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                overflow: 'hidden',
                background: '#fff',
              }}
            >
              <Row
                gutter={8}
                style={{
                  background: '#f8fafc',
                  borderBottom: '1px solid #e5e7eb',
                  padding: '10px 9px 10px 12px',
                  alignItems: 'center',
                }}
              >
                <Col span={8}><div style={tableHeaderStyle}>Week</div></Col>
                <Col span={8}><div style={tableHeaderStyle}>Extension (°)</div></Col>
                <Col span={8}><div style={tableHeaderStyle}>Flexion (°)</div></Col>
              </Row>
              {PROGRESS_GOAL_WEEKS.map((week, idx) => {
                const isSelected = selectedWeek === week
                const isLast = idx === PROGRESS_GOAL_WEEKS.length - 1
                const overridden =
                  cellOverridden(week, 'extension') ||
                  cellOverridden(week, 'flexion')
                const cell = (field) => (
                  <InputNumber
                    value={cellValue(week, field)}
                    onChange={(val) => {
                      setCell(week, field, val)
                      setSelectedWeek(week)
                    }}
                    onFocus={() => setSelectedWeek(week)}
                    style={
                      cellOverridden(week, field)
                        ? { width: '100%', borderColor: '#1890ff' }
                        : { width: '100%' }
                    }
                  />
                )
                return (
                  <div
                    key={week}
                    onClick={() => setSelectedWeek(week)}
                    style={{
                      cursor: 'pointer',
                      background: isSelected ? '#f0fdf4' : '#fff',
                      borderLeft: isSelected
                        ? '3px solid #22c55e'
                        : '3px solid transparent',
                      borderBottom: isLast ? 'none' : '1px solid #f1f5f9',
                      padding: '10px 9px 10px 12px',
                    }}
                  >
                    <Row gutter={8} style={{ alignItems: 'center' }}>
                      <Col span={8}>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: isSelected
                              ? '#15803d'
                              : overridden
                              ? '#1890ff'
                              : '#1f2937',
                            display: 'flex',
                            alignItems: 'center',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {week === -1 ? 'Pre-Op' : `Week ${week}`}
                          {overridden ? <span style={overriddenDot} /> : null}
                        </div>
                      </Col>
                      <Col span={8}>{cell('extension')}</Col>
                      <Col span={8}>{cell('flexion')}</Col>
                    </Row>
                  </div>
                )
              })}
            </div>
            <div style={{ fontSize: 11, color: '#888', marginTop: 8 }}>
              Each cell shows this patient's goal — the clinic's setting for that
              week where set, otherwise the standard recovery goal. Edit a cell to
              override it for this patient; clearing a cell restores the inherited
              value. Values are clinical degrees (0° = full extension, 90° =
              seated rest). Click a row to preview it.
            </div>
            {overrides.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <a
                  style={{ fontSize: 12, color: '#64748b' }}
                  onClick={() => setOverrides([])}
                >
                  ↺ Reset all to defaults
                </a>
              </div>
            )}
          </div>
        </Col>

        <Col
          flex='0 0 340px'
          style={{ alignSelf: 'flex-start', position: 'sticky', top: 0 }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              marginBottom: 6,
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 700, color: '#555' }}>
              PATIENT PREVIEW
            </div>
            <div style={{ fontSize: 11, color: '#52c41a', fontWeight: 600 }}>
              {selectedWeek === -1 ? 'Pre-Op' : `Week ${selectedWeek}`}
            </div>
          </div>
          <div
            style={{
              background: '#f5f5f5',
              border: '1px solid #d9d9d9',
              borderRadius: 8,
              padding: 0,
              fontFamily: 'Roboto, sans-serif',
              overflow: 'hidden',
            }}
          >
            <div style={{ padding: '14px 18px 6px' }}>
              <ImageSwapFigure
                assetKind='HEEL_SLIDE_ROM'
                liveAngle={figureAngle}
                labelAngle={previewAngle}
                bandRanges={{ yellow: [0, 130] }}
                yellowOutline
                goalAngles={goalAngles}
                maxWidth={240}
              />
            </div>

            <div
              style={{
                margin: '0 14px 10px',
                padding: '8px 10px',
                background: '#fff8e1',
                border: '1px solid #ffe9a8',
                borderRadius: 6,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 11,
                  color: '#8a6d3b',
                  fontWeight: 600,
                  marginBottom: 4,
                }}
              >
                <span>PREVIEW ANGLE</span>
                <span>{Math.round(previewAngle != null ? previewAngle : 0)}°</span>
              </div>
              <input
                type='range'
                min={sliderMin}
                max={sliderMax}
                step={1}
                value={previewAngle != null ? previewAngle : 0}
                onChange={(e) => setPreviewAngle(parseFloat(e.target.value))}
                style={{ width: '100%', transform: 'scaleX(-1)' }}
              />
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 10,
                  color: '#a08850',
                  marginTop: 2,
                }}
              >
                <span>← flexion ({sliderMax}°)</span>
                <span>extension ({sliderMin}°) →</span>
              </div>
            </div>
          </div>

          <div
            style={{ fontSize: 11, color: '#888', marginTop: 8, lineHeight: 1.5 }}
          >
            Goal ({selectedWeek === -1 ? 'pre-op' : `week ${selectedWeek}`}):{' '}
            extension <b>{goalExt != null ? `${goalExt}°` : '—'}</b> · flexion{' '}
            <b>{goalFlex != null ? `${goalFlex}°` : '—'}</b>
          </div>
        </Col>
      </Row>
    </Modal>
  )
}

ProgressGoalsModal.propTypes = {
  visible: PropTypes.bool,
  patient: PropTypes.object,
  editpage: PropTypes.bool,
  initialCriteria: PropTypes.object,
  initialOverrides: PropTypes.array,
  onCancel: PropTypes.func,
  onSave: PropTypes.func,
  reduxCustomerId: PropTypes.string,
}

const mapStateToProps = (state) => ({
  reduxCustomerId:
    state.profile && state.profile.customer && state.profile.customer._id,
})

export default connect(mapStateToProps)(ProgressGoalsModal)
